import { noop } from 'lodash'
import { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { useGetByTenantAccountQuery } from '@/api'
import { Rule } from '@/components/core-v2'
import {
  CheckboxField,
  ClearButton,
  DropdownMenu,
  Legend,
  RadioFieldSet,
  SearchField
} from '@/components/core-v2/'
import { TENANT } from '@/const'
import { useNotesFiltersContext } from '@/routing/Reporting'

import classes from './index.module.scss'
import { Loading } from './loading'
import { Tree } from './tree'

export interface INoteFiltersForm extends Record<string, string | boolean> {
  collateralNo?: boolean
  collateralYes?: boolean
  'notes-currency'?: 'all' | 'zar'
  'notes-institutional'?: boolean
  'notes-retail'?: boolean
}

const FORM_DEFAULT_VALUES: INoteFiltersForm = {
  'notes-currency': 'all',
  'notes-institutional': false,
  'notes-retail': false,
  collateralYes: false,
  collateralNo: false
}

export const NotesFilters = () => {
  const { isLoading } = useGetByTenantAccountQuery({
    tenant: TENANT
  })

  const { filters, setFilters } = useNotesFiltersContext()

  const form = useForm<INoteFiltersForm>({
    defaultValues: { ...FORM_DEFAULT_VALUES, ...filters }
  })

  const formValues = form.watch()
  const areAnyFiltersSelected = Object.values(formValues).some(
    value => value === true || value === 'ZAR'
  )

  const onClearClick = useCallback(() => {
    form.reset()
    setFilters({})
  }, [form, setFilters])

  if (isLoading) return <Loading />

  const onClose = () => {
    setFilters(formValues)
  }

  const label = `Note filters (${areAnyFiltersSelected ? 'Filters selected' : 'All'})`

  return (
    <div className={classes.root}>
      <DropdownMenu id="notes-filter-menu" label={label} onClose={onClose}>
        <FormProvider {...form}>
          <form className={classes.form} onSubmit={form.handleSubmit(noop)}>
            <RadioFieldSet
              legend="Currency"
              name="notes-currency"
              options={[
                { label: 'All trades', value: 'all' },
                { label: 'South Africa Rand trades (ZAR) only', value: 'ZAR' }
              ]}
            />
            <Rule className="mt-4 mb-6" />
            <fieldset className={classes.clientType}>
              <Legend>Client type</Legend>
              <CheckboxField className="mr-4" id="notes-institutional" label="Institutional" />
              <CheckboxField id="notes-retail" label="Retail" />
            </fieldset>
            <Rule className="mt-4 mb-6" />
            <fieldset className={classes.clientType}>
              <Legend>Collateral for loan</Legend>
              <CheckboxField className="mr-4" id="collateralYes" label="Yes" />
              <CheckboxField id="collateralNo" label="No" />
            </fieldset>
            <Rule className="mt-4 mb-6" />
            <SearchField
              data-private
              className={classes.searchField}
              debounceMs={200}
              id="note-filters-search"
            />
            <Tree />
          </form>
        </FormProvider>
      </DropdownMenu>

      {areAnyFiltersSelected && (
        <ClearButton onClick={onClearClick}>Clear note filters</ClearButton>
      )}
    </div>
  )
}
